// src/pages/healthcare/appointments/appDrawerComponents/ScreeningPanel.tsx
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Collapse, Typography } from "antd";

import { fetchAppointmentScreening } from "../../../../api/healthcare/ScreeningApi";
import { DateTimeUtils } from "../../../../utils/userPrefs/DateTimeUtils";

const { Text } = Typography;

interface ScreeningPanelProps {
	appointmentId: number;
}

/**
 * S10 (ClickUp 86d44jbyy) — what the patient answered before booking. A plain read: acting on an
 * answer (writing it into medical history, flagging it in the consult) is a separate ticket.
 */
const ScreeningPanel: React.FC<ScreeningPanelProps> = ({ appointmentId }) => {
	const { data: screening } = useQuery({
		queryKey: ["appointmentScreening", appointmentId],
		queryFn: () => fetchAppointmentScreening(appointmentId),
		enabled: appointmentId > 0,
	});

	// Covers both "still loading" and "never screened" — most appointments have no questionnaire,
	// so anything rendered here by default would be clutter on the majority of the drawer's uses.
	if (!screening) return null;

	const answeredAt = DateTimeUtils.formatLocalDateTime(screening.created_at);

	return (
		<Collapse
			size="small"
			// Open by default: the point of asking is that the practitioner reads it before the
			// consult, and it only renders at all on the minority of appointments that were screened.
			defaultActiveKey={["screening"]}
			style={{ marginBottom: 8 }}
			data-testid="appointment-screening-panel"
			items={[
				{
					key: "screening",
					label: "Pre-booking screening answers",
					children: (
						<>
							{screening.answers.map((answer, index) => (
								<div key={`${answer.question_id}-${index}`} style={{ marginBottom: 8 }}>
									<div style={{ fontSize: 12, color: "var(--mg-text-secondary)" }}>{answer.question_text}</div>
									<Text strong style={{ fontSize: 13 }}>
										{answer.selected.length > 0 ? answer.selected.map((option) => option.label).join(", ") : "Not answered"}
									</Text>
								</div>
							))}
							{answeredAt && (
								<Text type="secondary" style={{ fontSize: 11 }}>
									Answered {answeredAt}
								</Text>
							)}
						</>
					),
				},
			]}
		/>
	);
};

export default ScreeningPanel;
